// src/Context/ChatContext.js
import React, { createContext, useContext, useEffect, useCallback, useReducer, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Import your Axios instances
import { api, AuthApi, uploadPortfolioApi, clearPortfolioApi } from '../Api/BaseApi';

const ChatContext = createContext();

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
export const formatDateString = (dateInput) => {
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "INVALID_DATE";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_USER_INFO: 'SET_USER_INFO', // New action to store user_id and username
  ADD_MESSAGE_START: 'ADD_MESSAGE_START',
  ADD_MESSAGE_SUCCESS: 'ADD_MESSAGE_SUCCESS',
  ADD_MESSAGE_FAILURE: 'ADD_MESSAGE_FAILURE',
  LOAD_MESSAGES_START: 'LOAD_MESSAGES_START',
  LOAD_MESSAGES_SUCCESS: 'LOAD_MESSAGES_SUCCESS',
  LOAD_MESSAGES_FAILURE: 'LOAD_MESSAGES_FAILURE',
  SET_SELECTED_QUESTION: 'SET_SELECTED_QUESTION',
  CLEAR_CHAT: 'CLEAR_CHAT',
  START_NEW_CHAT: 'START_NEW_CHAT', // blank the view AND suppress auto-reload of today's messages
  EXIT_NEW_CHAT: 'EXIT_NEW_CHAT',   // user sent a message or picked a date -> resume normal loading
  UPDATE_MESSAGE_ANSWER: 'UPDATE_MESSAGE_ANSWER',
  LOGOUT_CLEANUP: 'LOGOUT_CLEANUP', // For resetting state on logout
};

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
const initialState = {
  messages: [],
  isLoading: false,
  selectedQuestionId: null,
  error: null,
  currentDateBeingViewed: formatDateString(new Date()),
  userId: null, // Will store the string "USR_..." ID
  username: null, // Store chat username
  isAuthenticated: AuthApi.isAuthenticated(),
  newChatMode: false, // when true, a fresh blank chat is open; do not auto-load today's saved messages
};

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// Reducer remains the same as your provided version
function chatReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ActionTypes.SET_USER_INFO:
      return { ...state, userId: action.payload.userId, username: action.payload.username, isAuthenticated: AuthApi.isAuthenticated() };
    case ActionTypes.ADD_MESSAGE_START:
      return { ...state, messages: [...state.messages, action.payload.tempMessage], error: null };
    case ActionTypes.UPDATE_MESSAGE_ANSWER:
      return { ...state, messages: state.messages.map(msg => msg.clientId === action.payload.clientId ? { ...msg, answer: action.payload.answer, status: 'answered' } : msg) };
    case ActionTypes.ADD_MESSAGE_SUCCESS:
      return { ...state, messages: state.messages.map(msg => msg.clientId === action.payload.clientId ? { ...msg, history_id: action.payload.savedDbRecord.history_id, timestamp: new Date(action.payload.savedDbRecord.created_at || msg.timestamp), is_user_message: action.payload.savedDbRecord.is_user_message, id: action.payload.idFromBackend, status: 'sent', clientId: undefined } : msg) };
    case ActionTypes.ADD_MESSAGE_FAILURE:
      return { ...state, messages: state.messages.map(msg => msg.clientId === action.payload.clientId ? { ...msg, status: 'error', errorDetails: action.payload.error, answer: `Failed: ${action.payload.error}` } : msg), error: action.payload.error };
    case ActionTypes.LOAD_MESSAGES_START:
      return { ...state, isLoading: true, messages: [], error: null, currentDateBeingViewed: action.payload.date };
    case ActionTypes.LOAD_MESSAGES_SUCCESS:
      return { ...state, isLoading: false, messages: action.payload.messages, error: null, currentDateBeingViewed: action.payload.date };
    case ActionTypes.LOAD_MESSAGES_FAILURE:
      return { ...state, isLoading: false, error: action.payload.error, messages: [], currentDateBeingViewed: action.payload.dateOnError || state.currentDateBeingViewed };
    case ActionTypes.SET_SELECTED_QUESTION:
      return { ...state, selectedQuestionId: action.payload };
    case ActionTypes.CLEAR_CHAT:
      return { ...state, messages: [], error: null };
    case ActionTypes.START_NEW_CHAT:
      return { ...state, messages: [], error: null, selectedQuestionId: null, newChatMode: true };
    case ActionTypes.EXIT_NEW_CHAT:
      return state.newChatMode ? { ...state, newChatMode: false } : state;
    case ActionTypes.LOGOUT_CLEANUP:
      return { ...initialState, isAuthenticated: false, currentDateBeingViewed: formatDateString(new Date()) }; // Reset to initial but keep today's date
    default:
      console.warn("Unhandled ChatContext action type:", action.type);
      return state;
  }
}
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialState,
    isAuthenticated: AuthApi.isAuthenticated(), // Initial check when provider mounts
  });

  const [isUserIdentified, setIsUserIdentified] = useState(AuthApi.isAuthenticated());
  // Current customer in conversation focus. Hydrated from sessionStorage so the
  // focus survives a page reload within the same browser session.
  const lastCustomerIdRef = useRef(
    (typeof window !== 'undefined' && window.sessionStorage.getItem('pinaka_focus_customer')) || null
  );
  const setFocusCustomer = useCallback((id) => {
    const norm = id ? String(id).toUpperCase() : null;
    lastCustomerIdRef.current = norm;
    try {
      if (norm) window.sessionStorage.setItem('pinaka_focus_customer', norm);
      else window.sessionStorage.removeItem('pinaka_focus_customer');
    } catch (e) { /* sessionStorage unavailable */ }
  }, []);

  // The portfolio uploaded in this session. Hydrated from sessionStorage so the
  // loaded portfolio survives a page reload (the authoritative copy lives
  // server-side as SessionPortfolio; this is the client-side awareness used to
  // hint the UI and keep continuity). Never cleared by New Chat — a new chat
  // does not unload the data file the user already uploaded this session.
  const _initialPortfolio = (() => {
    try {
      const raw = typeof window !== 'undefined'
        && window.sessionStorage.getItem('pinaka_session_portfolio');
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  })();
  const sessionPortfolioRef = useRef(_initialPortfolio);
  // State mirror so the "Portfolio loaded" badge re-renders the moment the
  // portfolio is set/cleared; the ref keeps synchronous reads available too.
  const [sessionPortfolio, setSessionPortfolioState] = useState(_initialPortfolio);
  const setSessionPortfolio = useCallback((portfolio) => {
    sessionPortfolioRef.current = portfolio || null;
    setSessionPortfolioState(portfolio || null);
    try {
      if (portfolio) {
        window.sessionStorage.setItem('pinaka_session_portfolio', JSON.stringify(portfolio));
      } else {
        window.sessionStorage.removeItem('pinaka_session_portfolio');
      }
    } catch (e) { /* sessionStorage unavailable */ }
  }, []);

  const navigate = useNavigate();

 
useEffect(() => {
  const hydrateOrSyncUserInfo = () => {
    const currentAuthStatusFromApi = AuthApi.isAuthenticated();
    // ... (initial logs for currentAuthStatusFromApi, state.isAuthenticated, state.userId are good)

    if (currentAuthStatusFromApi) {
      if (!state.userId || !state.isAuthenticated) {
        const userInfoFromStorage = AuthApi.getUserInfo();

        let chatUserIdToSet = null;
        let chatUsernameToSet = null;

        if (userInfoFromStorage && typeof userInfoFromStorage === 'object') {
          // Priority 1: Check for the nested structure first, as it's often more explicit
          if (userInfoFromStorage.user && typeof userInfoFromStorage.user === 'object' && userInfoFromStorage.user.chatUserId) {
            chatUserIdToSet = userInfoFromStorage.user.chatUserId;
            chatUsernameToSet = userInfoFromStorage.user.chatUsername || userInfoFromStorage.user.email;
          }
          // Priority 2: If not in nested, check for top-level (as your current working version does)
          else if (userInfoFromStorage.chatUserId) {
            chatUserIdToSet = userInfoFromStorage.chatUserId;
            chatUsernameToSet = userInfoFromStorage.chatUsername || userInfoFromStorage.email;
          } else {
            console.warn("ERROR: something went wrong")

            // warn("ChatContext: hydrateOrSync - chatUserId key not found in expected locations (neither nested user.chatUserId nor top-level chatUserId).");
          }
        } else {
          console.warn("ERROR: something went wrong")
          // warn("ChatContext: hydrateOrSync - AuthApi.getUserInfo() did not return a valid object.");
        }

        if (chatUserIdToSet) {
          dispatch({
            type: ActionTypes.SET_USER_INFO,
            payload: { userId: chatUserIdToSet, username: chatUsernameToSet }
          });
        } else {
          console.warn("ERROR: something went wrong")
          // warn("ChatContext: hydrateOrSync - Token exists, but chatUserId could not be extracted. Logging out.");
          dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
        }
      } else {
        // console.warn("ERROR: something went wrong")
        // ... (no action needed log)
      }
    } else {
      // console.warn("ERROR: something went wrong")
      // ... (logout cleanup if needed log)
    }
  };

  hydrateOrSyncUserInfo();
  window.addEventListener('storage', hydrateOrSyncUserInfo);
  // ... (return and dependencies)
}, [state.isAuthenticated, state.userId, dispatch]);

// Separate useEffect for isUserIdentified
useEffect(() => {
    setIsUserIdentified(!!state.userId && state.isAuthenticated);
}, [state.userId, state.isAuthenticated]);


// And ensure setIsUserIdentified is kept in sync, perhaps in another effect:
useEffect(() => {
    setIsUserIdentified(!!state.userId && state.isAuthenticated);
}, [state.userId, state.isAuthenticated]);

  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // add conversation turn means this is the main function to add the message to the chat 
  // and it is used in ChatInput component
  // it is a useCallback function so that it is not redefined on every render
  // and so it is memoized means it is cached
  // so that it is not redefined on every render
  const addConversationTurn = useCallback(async (questionText, queryApiFunction) => {
    // this two variable clientId and contentId are used to identify the message
    // clientId is used to identify the message in the chat
    // contentId is used to identify the message in the database
    const clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const contentId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const currentMessageDate = formatDateString(new Date());

    const tempMessage = {
      clientId,
      id: contentId,
      question: questionText,
      answer: 'Thinking...',
      timestamp: new Date(),
      is_user_message: true,
      status: 'pending_answer',
    };
    dispatch({ type: ActionTypes.ADD_MESSAGE_START, payload: { tempMessage } });
    dispatch({ type: ActionTypes.EXIT_NEW_CHAT }); // user is now chatting -> resume normal loading
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    // if (!AuthApi.isAuthenticated()) {
    //   const authError = "Authentication required. Please log in to chat.";
    //   dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: authError } });
    //   dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    //   return;
    // }
    if (!state.userId) { // Check for chat-specific userId
      const authError = "User not identified for chat. Please log in again.";
      dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: authError } });
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      return;
    }



    // here we are getting the answer from the api
    // queryApiFunction is the function that is passed from ChatInput
    // it is an async function that returns the answer
    // it is used to get the answer from the api
    // Track the customer currently in focus so follow-up questions and action
    // verbs ("recommendations to fix these", "send the notification") resolve
    // without re-stating the id. A customer named in this message takes priority;
    // otherwise the prior focus is carried in.
    const custMatch = (questionText || '').match(/CUST\d+/i);
    if (custMatch) setFocusCustomer(custMatch[0].toUpperCase());

    let answerText;
    try {
      const apiResult = await queryApiFunction(questionText, lastCustomerIdRef.current);
      // queryApi returns { answer, customerId }; other callers may return a string.
      answerText = (apiResult && typeof apiResult === 'object') ? apiResult.answer : apiResult;
      const resolvedCust = (apiResult && typeof apiResult === 'object') ? apiResult.customerId : null;
      // Persist the customer this turn resolved to (from context) so the next
      // turn keeps continuity even though the user never re-typed the id.
      if (resolvedCust) setFocusCustomer(resolvedCust);
      dispatch({ type: ActionTypes.UPDATE_MESSAGE_ANSWER, payload: { clientId, answer: answerText } });
    } catch (queryError) {
      const errMsg = queryError.response?.data?.message || queryError.message || "Failed to get an answer from API.";
      console.error('Query API Error:', queryError);
      dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: errMsg } });
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      return;
    }

    try {
      // here we are saving the message in the database
      // qaPairToSaveInMessageText is the object that is saved in the database
      const qaPairToSaveInMessageText = { question: questionText, answer: answerText, id: contentId };
      // Use FastAPI proxy for saving messages
      const response = await api.post('/auth/api/chat/message', {
        message_text: JSON.stringify(qaPairToSaveInMessageText),
        is_user_message: true,
        user_id: state.userId,
        session_date: currentMessageDate
      }, { withCredentials: true });

      // Axios response data is in response.data
      const savedResult = response.data;
      // savedResult is the object that is returned from the database
      // it contains the history_id of the message
      // history_id is the id of the message in the database
      // it is used to identify the message in the database

      if (!savedResult.success || !savedResult.data || !savedResult.data.history_id) {
        throw new Error(savedResult.message || "Backend failed to save message or return valid data.");
      }
      dispatch({
        type: ActionTypes.ADD_MESSAGE_SUCCESS,
        payload: { clientId, savedDbRecord: savedResult.data, idFromBackend: contentId },
      });
    } catch (saveError) {
      const errMsg = saveError.response?.data?.message || saveError.message || "Failed to save conversation.";
      console.error('Save to Backend Error:', saveError);
      dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: errMsg } });
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [state.userId, setFocusCustomer]); // No need for state.currentDateBeingViewed dependency as it's not used in the function

  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // Add a local, already-resolved assistant message (no API call). Used for
  // client-side notices such as an unsupported-file-type error, shown inline in
  // the chat exactly like a normal answer. Not persisted to history.
  const addAssistantNotice = useCallback((questionLabel, answerText) => {
    const clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const contentId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    dispatch({
      type: ActionTypes.ADD_MESSAGE_START,
      payload: {
        tempMessage: {
          clientId, id: contentId, question: questionLabel,
          answer: answerText, timestamp: new Date(),
          is_user_message: true, status: 'answered',
        },
      },
    });
    dispatch({ type: ActionTypes.EXIT_NEW_CHAT });
    dispatch({ type: ActionTypes.UPDATE_MESSAGE_ANSWER, payload: { clientId, answer: answerText } });
  }, []);

  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // Upload a portfolio file (XLSX/XLS/TXT/MD) -> batch predictions + prescriptions
  const addUploadTurn = useCallback(async (file, send = false) => {
    const clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const contentId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const currentMessageDate = formatDateString(new Date());
    const questionLabel = `📎 Uploaded portfolio: ${file.name}${send ? ' (auto-send ON)' : ''}`;

    const tempMessage = {
      clientId,
      id: contentId,
      question: questionLabel,
      answer: send ? 'Analyzing portfolio and sending notifications…' : 'Analyzing portfolio…',
      timestamp: new Date(),
      is_user_message: true,
      status: 'pending_answer',
    };
    dispatch({ type: ActionTypes.ADD_MESSAGE_START, payload: { tempMessage } });
    dispatch({ type: ActionTypes.EXIT_NEW_CHAT }); // user is now chatting -> resume normal loading
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    if (!state.userId) {
      dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: 'User not identified for chat. Please log in again.' } });
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      return;
    }

    let answerText;
    try {
      const uploadResult = await uploadPortfolioApi(file, send);
      // uploadPortfolioApi returns { answer, filename, customerIds, analyzed }.
      answerText = (uploadResult && typeof uploadResult === 'object') ? uploadResult.answer : uploadResult;
      // Remember the loaded portfolio for the rest of the session so later chat
      // prompts (e.g. "send me the portfolio summary report") have context. The
      // backend also persists this server-side; this keeps the client in sync.
      if (uploadResult && typeof uploadResult === 'object' && uploadResult.filename) {
        setSessionPortfolio({
          filename: uploadResult.filename,
          customerIds: uploadResult.customerIds || [],
          analyzed: uploadResult.analyzed || 0,
          totalCustomers: uploadResult.totalCustomers ?? (uploadResult.customerIds || []).length,
          totalInvoices: uploadResult.totalInvoices || 0,
          uploadedAt: new Date().toISOString(),
        });
      }
      dispatch({ type: ActionTypes.UPDATE_MESSAGE_ANSWER, payload: { clientId, answer: answerText } });
    } catch (uploadError) {
      const errMsg = uploadError.response?.data?.detail || uploadError.message || 'Failed to analyze the uploaded file.';
      dispatch({ type: ActionTypes.ADD_MESSAGE_FAILURE, payload: { clientId, error: errMsg } });
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      return;
    }

    try {
      const qaPair = { question: questionLabel, answer: answerText, id: contentId };
      const response = await api.post('/auth/api/chat/message', {
        message_text: JSON.stringify(qaPair),
        is_user_message: true,
        user_id: state.userId,
        session_date: currentMessageDate,
      }, { withCredentials: true });
      const savedResult = response.data;
      if (savedResult.success && savedResult.data?.history_id) {
        dispatch({ type: ActionTypes.ADD_MESSAGE_SUCCESS, payload: { clientId, savedDbRecord: savedResult.data, idFromBackend: contentId } });
      }
    } catch (saveError) {
      // Analysis already shown; persistence failure is non-fatal for the view.
      console.error('Save upload turn failed:', saveError);
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [state.userId, setSessionPortfolio]);

  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // load message for date
  const loadMessagesForDate = useCallback(async (dateStrToLoad) => {
    // here we are loading the messages for a specific date
    // dateStrToLoad is the date for which the messages are to be loaded
    if (!dateStrToLoad || dateStrToLoad === "INVALID_DATE") {
      dispatch({ type: ActionTypes.LOAD_MESSAGES_FAILURE, payload: { error: "Invalid date provided.", dateOnError: dateStrToLoad } });
      return;
    }
    dispatch({ type: ActionTypes.EXIT_NEW_CHAT }); // explicitly viewing a date ends the blank-chat state
    dispatch({ type: ActionTypes.LOAD_MESSAGES_START, payload: { date: dateStrToLoad } });

    // if (!AuthApi.isAuthenticated()) {
    //   const authError = "Authentication required to load history.";
    //   dispatch({ type: ActionTypes.LOAD_MESSAGES_FAILURE, payload: { error: authError, dateOnError: dateStrToLoad } });
    //   // SET_LOADING to false is handled by LOAD_MESSAGES_FAILURE in the reducer
    //   return;
    // }
    if (!state.userId) { // Check for chat-specific userId
      const authError = "User not identified. Cannot load history.";
      dispatch({ type: ActionTypes.LOAD_MESSAGES_FAILURE, payload: { error: authError, dateOnError: dateStrToLoad } });
      return;
    }


    try {
      // Use FastAPI proxy for fetching history
      const response = await api.get(`/auth/api/chat/history/${dateStrToLoad}?userId=${state.userId}`, { withCredentials: true });
      const rawData = response.data; // Axios response data

      if (!rawData.success) throw new Error(rawData.message || 'API Error loading history');
      // rawData is the object that is returned from the database

      // processedMessages is the array of messages that is processed from the database
      // it is used to display the messages in the chat
      const processedMessages = (rawData.data || []).map(dbRecord => {
        try {
          // dbRecord is the object that is returned from the database
          // it contains the history_id, message_text, created_at, is_user_message
          const messageData = JSON.parse(dbRecord.message_text);
          return {
            history_id: dbRecord.history_id,
            id: messageData.id,
            question: messageData.question,
            answer: messageData.answer,
            timestamp: new Date(dbRecord.created_at),
            is_user_message: dbRecord.is_user_message,
            status: 'sent',
          };
        } catch (e) { console.error("Error parsing message_text in loadMessagesForDate", e, dbRecord); return null; }
      }).filter(Boolean);
      dispatch({ type: ActionTypes.LOAD_MESSAGES_SUCCESS, payload: { messages: processedMessages, date: dateStrToLoad } });
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Failed to load messages.";
      console.error(`ChatContext: Error loading messages for ${dateStrToLoad}:`, error);
      dispatch({ type: ActionTypes.LOAD_MESSAGES_FAILURE, payload: { error: errMsg, dateOnError: dateStrToLoad } });
    }
  }, [state.userId]);

  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // useEffect(() => {
  //   if (initialState.currentDateBeingViewed !== "INVALID_DATE") {
  //     loadMessagesForDate(initialState.currentDateBeingViewed);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // loadMessagesForDate is stable due to useCallback

  // useEffect(() => {
  //   // Load initial messages only if user is identified and date is valid
  //   if (isUserIdentified && initialState.currentDateBeingViewed !== "INVALID_DATE") {
  //     loadMessagesForDate(initialState.currentDateBeingViewed);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isUserIdentified, loadMessagesForDate]); // Depend on isUserIdentified
  // // Effect to load initial messages when user becomes identified

  useEffect(() => {
    if (state.userId && state.currentDateBeingViewed !== "INVALID_DATE") { // Use state.userId
      loadMessagesForDate(state.currentDateBeingViewed);
    } else if (!state.userId) {
      // Clear messages if user logs out or is not identified
      dispatch({ type: ActionTypes.CLEAR_CHAT });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.userId, state.currentDateBeingViewed]); // Removed loadMessagesForDate from deps to avoid loop if it's not stable


  const setSelectedQuestion = useCallback((questionDisplayId) => {
    dispatch({ type: ActionTypes.SET_SELECTED_QUESTION, payload: questionDisplayId });
  }, []);

  const clearChatForCurrentDate = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_CHAT });
    setFocusCustomer(null); // a fresh chat starts with no customer in focus
  }, [setFocusCustomer]);

  // Open a brand-new, blank chat. Blanks the view and suppresses the auto-reload
  // of today's saved messages until the user sends a message or opens a date from
  // History. The prior chat remains saved per date and is reachable from History.
  const startNewChat = useCallback(() => {
    dispatch({ type: ActionTypes.START_NEW_CHAT });
    setFocusCustomer(null);
    // New Chat unloads the session portfolio — clear it on the client AND tell the
    // server to stop injecting its context. (A new message / report / auto-send
    // toggle must NOT clear it; only New Chat or a fresh upload does.)
    setSessionPortfolio(null);
    clearPortfolioApi();
  }, [setFocusCustomer, setSessionPortfolio]);

  const setLoading = useCallback((isLoading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
  }, []);


  // const handleLoginSuccess = useCallback((userData) => {
  //   dispatch({
  //     type: ActionTypes.SET_USER_INFO,
  //     payload: { userId: userData.chatUserId, username: userData.chatUsername || userData.email }
  //   });
  //   setIsUserIdentified(true);
  // }, [dispatch]);
  const handleLoginSuccess = useCallback((userData) => {
    if (!userData || !userData.chatUserId) {
      console.error("ChatContext: handleLoginSuccess - chatUserId is missing in userData!", userData);
    }
    dispatch({
      type: ActionTypes.SET_USER_INFO,
      payload: { userId: userData.chatUserId, username: userData.chatUsername || userData.email }
    });
    setIsUserIdentified(true);
  }, [dispatch]);

  const handleLogout = useCallback(async () => { // Make it async if AuthApi.logout is async
    try {
      await AuthApi.logout(); // AuthApi.logout already clears cookies
      dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
      setFocusCustomer(null);
      setSessionPortfolio(null); // the loaded portfolio is per-session — drop it on logout
      setIsUserIdentified(false);
      navigate('/login'); // Navigate after state update
    } catch (error) {
      error("Logout failed:", error);
      // Still attempt to clean up UI state
      dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
      setFocusCustomer(null);
      setSessionPortfolio(null);
      setIsUserIdentified(false);
    }
  }, [dispatch, navigate, setFocusCustomer, setSessionPortfolio]);

  const handleLogoutAll = useCallback(async () => {
    try {
      await AuthApi.logoutAll();
      dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
      setFocusCustomer(null);
      setSessionPortfolio(null);
      setIsUserIdentified(false);
      navigate('/login');
    } catch (error) {
      error('Logout all failed:', error);
      dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
      setFocusCustomer(null);
      setSessionPortfolio(null);
      setIsUserIdentified(false);
    }
  }, [dispatch, navigate, setFocusCustomer, setSessionPortfolio]);

  // const handleLogout = useCallback(() => {
  //   AuthApi.logout().then(() => {
  //       dispatch({ type: ActionTypes.LOGOUT_CLEANUP });
  //       setIsUserIdentified(false);
  //       // Optionally redirect or clear other app state
  //       // window.location.href = '/login';
  //   });
  // }, []);


  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  // ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
  const value = {
    messages: state.messages,
    isLoading: state.isLoading,
    selectedQuestionId: state.selectedQuestionId,
    error: state.error,
    currentDateBeingViewed: state.currentDateBeingViewed,
    userId: state.userId, // Expose userId
    username: state.username, // Expose username
    isUserIdentified, // Expose identification status
    isAuthenticated: state.isAuthenticated,
    newChatMode: state.newChatMode, // true while a fresh blank chat is open
    sessionPortfolio, // the portfolio uploaded this session (or null) — drives the "Portfolio loaded" badge
    addConversationTurn,
    addUploadTurn,
    addAssistantNotice,
    loadMessagesForDate,
    setSelectedQuestion,
    clearChatForCurrentDate,
    startNewChat,
    setLoading,
    formatDateString,
    handleLoginSuccess,
    handleLogout,
    handleLogoutAll,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};